import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { useListIdentifiers } from 'src/features/membership/api/identifier/useListIdentifiers';
import { MembershipIdentifier } from 'src/features/membership/types';
import { identifierTypeBadgeOptions } from 'src/features/membership/utils/constants';
import { CellBadge, DataTable, DataTableControls, DataTablePagination } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import CreateIdentifierFormModal from 'src/features/membership/components/forms/identifier/CreateIdentifierFormModal';
import { identifiersMessages } from 'src/features/membership/utils/messages';
import CellIdentifierActions from 'src/features/membership/components/cell/CellIdentifierActions';
import { PATH } from 'src/features/navigation/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import usePagination from 'src/registry/hooks/usePagination';
import useFilters from 'src/registry/hooks/useFilters';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

const columnHelper = createColumnHelper<MembershipIdentifier>();

type Props = {
    membershipId?: string;
};
export default function IdentifiersTable({ membershipId }: Props) {
    const { formatMessage } = useIntl();
    const permissionAccessActiveStay = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const permissionManageMembershipIdentifiers = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);

    const { querySearchOptions, ...search } = useSearch();
    const { querySortingOptions, ...sorting } = useSorting();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();

    const { parkingId } = useParking();
    const { data, isLoading } = useListIdentifiers({
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        parkingId,
        membershipId: membershipId ?? '',
        config: { enabled: !!membershipId },
    });

    const columns = [
        columnHelper.accessor('identifier.value', {
            enableSorting: true,
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(identifiersMessages.identifier),
            },
        }),
        columnHelper.accessor('identifier.type', {
            enableSorting: true,
            enableColumnFilter: true,
            header: DataTableColumnHeader,
            cell: CellBadge,
            meta: {
                title: formatMessage(identifiersMessages.identifierType),
                badgeOptions: identifierTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('current_stay', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(identifiersMessages.activeStay),
            },
            cell: ({ row: { original } }) => {
                if (!original.current_stay) return '-';
                return (
                    <LinkWithIcon
                        to={`/${PATH.VISITORS}/active-stays/dashboard/${original.current_stay.id}`}
                        linkTitle={original.current_stay.ref}
                        hasPermissions={permissionAccessActiveStay}
                    />
                );
            },
        }),
        columnHelper.accessor('owner.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(identifiersMessages.owner),
            },
        }),
        columnHelper.accessor('public_notes', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(identifiersMessages.publicNotes),
            },
        }),
        columnHelper.accessor('private_notes', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(identifiersMessages.privateNotes),
            },
        }),
        ...(permissionManageMembershipIdentifiers
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: (props) => <CellIdentifierActions {...props} />,
                  }),
              ]
            : []),
    ];
    return (
        <DataTable<MembershipIdentifier, any>
            search={search}
            sorting={sorting}
            pagination={pagination}
            filters={filters}
            isLoading={isLoading}
            columns={columns}
            data={data}
            semantics={{
                title: formatMessage(identifiersMessages.identifiers),
                entryName: formatMessage(identifiersMessages.identifier).toLowerCase(),
                entryNames: formatMessage(identifiersMessages.identifiers).toLowerCase(),
                placeholder: formatMessage(identifiersMessages.identifier),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageMembershipIdentifiers && { newEntry: CreateIdentifierFormModal }),
            }}
        />
    );
}
